import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, X, GripVertical, Lock, Bell, Upload, Camera, Smile, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { createList, EMOJI_SUGGESTIONS, LIST_TYPE_LABELS } from '../lib/lists';
import { PrimaryButton } from '../components/Button';
import { TextInput } from '../components/Input';

export default function NewList() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [type, setType] = useState(params.get('type') || 'elementos');
    const [icon, setIcon] = useState('📝');
    const [name, setName] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [items, setItems] = useState([]);
    const [itemDraft, setItemDraft] = useState('');
    const [noteContent, setNoteContent] = useState('');
    const [contacts, setContacts] = useState([]);
    const [members, setMembers] = useState([]);
    const [sharedWith, setSharedWith] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    useEffect(() => {
        api.get('/family/members').then((r) => setMembers(r.data.members || [])).catch(() => {});
    }, []);

    const addItem = () => {
        const t = itemDraft.trim();
        if (!t) return;
        setItems([...items, { id: crypto.randomUUID(), text: t }]);
        setItemDraft('');
    };

    const submit = async () => {
        setError('');
        if (!name.trim()) {
            setError('Ponle un nombre a tu lista');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                type,
                name: name.trim(),
                icon,
                is_private: isPrivate,
                shared_with: sharedWith,
                items: type === 'elementos' ? items.map((i) => ({ text: i.text })) : [],
                note_content: type === 'nota' ? noteContent : null,
                contacts: type === 'contactos' ? contacts.map((c) => ({
                    name: c.name, role: c.role || null, phone: c.phone || null, email: c.email || null,
                })) : [],
            };
            const created = await createList(payload);
            navigate(`/listas/${created.id}`, { replace: true });
        } catch (e) {
            setError(e?.response?.data?.detail || 'No pudimos crear la lista');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleMember = (id) => {
        setSharedWith((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
    };

    return (
        <div className="min-h-screen bg-surface app-shell pb-32">
            <div className="px-6 pt-4">
                <div className="flex items-center justify-between">
                    <button onClick={() => navigate(-1)} className="w-10 h-10 -ml-2 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="new-list-back">
                        <ArrowLeft className="w-5 h-5 text-brand-navy" />
                    </button>
                    <h1 className="text-base font-bold text-brand-navy">Nueva Lista</h1>
                    <button
                        onClick={submit}
                        disabled={submitting}
                        className="px-4 h-9 rounded-full bg-brand text-white text-sm font-bold disabled:opacity-50"
                        data-testid="new-list-save"
                    >
                        Guardar
                    </button>
                </div>

                {error && <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-xl" data-testid="new-list-error">{error}</div>}

                <Field label="Tipo de lista">
                    <Select value={type} onChange={setType} options={[
                        { value: 'elementos', label: '☑️ Elementos (checklist)' },
                        { value: 'nota', label: '📝 Nota rápida' },
                        { value: 'contactos', label: '👥 Contactos' },
                    ]} testId="new-list-type" />
                </Field>

                <Field label="Ícono">
                    <button
                        onClick={() => setShowEmojiPicker(true)}
                        className="w-full h-14 bg-white border border-gray-200 rounded-2xl flex items-center justify-between px-4 hover:border-brand transition"
                        data-testid="new-list-icon-picker"
                    >
                        <span className="flex items-center gap-3">
                            <span className="text-2xl">{icon}</span>
                            <span className="text-sm text-gray-600">Toca para cambiar</span>
                        </span>
                        <Smile className="w-5 h-5 text-gray-400" />
                    </button>
                </Field>

                <Field label="Nombre de la lista">
                    <TextInput
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ej: Supermercado"
                        testId="new-list-name"
                    />
                </Field>

                {type === 'elementos' && (
                    <>
                        <Field label="Elementos" rightLabel={`${items.length} elemento${items.length !== 1 ? 's' : ''}`}>
                            <div className="space-y-2">
                                {items.map((it, idx) => (
                                    <div key={it.id} className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-3 py-2" data-testid={`new-list-item-${idx}`}>
                                        <GripVertical className="w-4 h-4 text-gray-300" />
                                        <span className="flex-1 text-sm text-gray-800">{it.text}</span>
                                        <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center">
                                            <X className="w-4 h-4 text-gray-400" />
                                        </button>
                                    </div>
                                ))}
                                <div className="flex items-center gap-2">
                                    <input
                                        value={itemDraft}
                                        onChange={(e) => setItemDraft(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && addItem()}
                                        placeholder="Escribe un elemento…"
                                        className="flex-1 h-12 bg-white border border-gray-200 rounded-2xl px-4 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20"
                                        data-testid="new-list-item-draft"
                                    />
                                    <button onClick={addItem} className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center shadow-brand-cta" data-testid="new-list-item-add">
                                        <Plus className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </Field>

                        <ComingSoonAttachments />
                    </>
                )}

                {type === 'nota' && (
                    <Field label="Contenido" rightLabel="Markdown soportado">
                        <textarea
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                            placeholder="Escribe tu nota… puedes usar listas con - o 1. 2. 3."
                            rows={8}
                            className="w-full bg-white border border-gray-200 rounded-2xl p-4 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 resize-y"
                            data-testid="new-list-note-content"
                        />
                    </Field>
                )}

                {type === 'contactos' && (
                    <ContactDraftSection contacts={contacts} setContacts={setContacts} />
                )}

                <div className="mt-6 bg-white rounded-2xl p-4 shadow-card">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="w-10 h-10 rounded-full bg-orange-100 text-orange-500 flex items-center justify-center">
                                <Lock className="w-5 h-5" />
                            </span>
                            <div>
                                <div className="text-sm font-bold text-brand-navy">Lista privada</div>
                                <div className="text-xs text-gray-500">Solo tú y los miembros invitados podrán verla</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsPrivate(!isPrivate)}
                            className={`relative w-11 h-6 rounded-full transition ${isPrivate ? 'bg-brand' : 'bg-gray-300'}`}
                            data-testid="new-list-private-toggle"
                        >
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition ${isPrivate ? 'left-5' : 'left-0.5'}`} />
                        </button>
                    </div>
                </div>

                <div className="mt-4 bg-white rounded-2xl p-4 shadow-card">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Bell className="w-4 h-4 text-gray-500" />
                            <h3 className="text-sm font-bold text-brand-navy">{isPrivate ? 'Compartir con (miembros con acceso)' : 'Notificar a'}</h3>
                        </div>
                        <button
                            className="text-xs font-semibold text-brand"
                            onClick={() => setSharedWith(members.map((m) => m.id))}
                            data-testid="new-list-select-all"
                        >
                            Seleccionar todos
                        </button>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                        {members.map((m) => {
                            const active = sharedWith.includes(m.id);
                            return (
                                <button
                                    key={m.id}
                                    onClick={() => toggleMember(m.id)}
                                    className="flex flex-col items-center gap-1 min-w-[60px]"
                                    data-testid={`new-list-share-${m.id}`}
                                >
                                    <span className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold border-2 transition ${active ? 'border-brand bg-brand/10 text-brand' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                                        {(m.name || '?').charAt(0).toUpperCase()}
                                    </span>
                                    <span className="text-[10px] text-gray-600 truncate w-full text-center">{(m.name || '').split(' ')[0]}</span>
                                </button>
                            );
                        })}
                        {members.length === 0 && (
                            <div className="text-xs text-gray-400 italic">Aún no hay miembros en la familia</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] bg-white border-t border-gray-100 px-6 py-3 z-30">
                <PrimaryButton onClick={submit} loading={submitting} testId="new-list-submit">
                    Crear lista
                </PrimaryButton>
            </div>

            {showEmojiPicker && (
                <EmojiPicker
                    current={icon}
                    onPick={(e) => { setIcon(e); setShowEmojiPicker(false); }}
                    onClose={() => setShowEmojiPicker(false)}
                />
            )}
        </div>
    );
}

function Field({ label, rightLabel, children }) {
    return (
        <div className="mt-5">
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-gray-800">{label}</label>
                {rightLabel && <span className="text-xs text-gray-400">{rightLabel}</span>}
            </div>
            {children}
        </div>
    );
}

function Select({ value, onChange, options, testId }) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full h-14 bg-white border border-gray-200 rounded-2xl px-4 pr-10 text-sm appearance-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            >
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
    );
}

function EmojiPicker({ current, onPick, onClose }) {
    const inputRef = useRef();
    const [custom, setCustom] = useState('');

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end" onClick={onClose}>
            <div
                className="w-full max-w-[480px] mx-auto bg-white rounded-t-3xl p-5 max-h-[70vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="emoji-picker"
            >
                <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
                <h3 className="text-base font-bold text-brand-navy mb-2">Elige un emoji</h3>
                <p className="text-xs text-gray-500 mb-4">Toca un emoji o usa tu teclado nativo para escribir cualquiera 👇</p>

                <input
                    ref={inputRef}
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="✍️ Escribe cualquier emoji…"
                    className="w-full h-14 bg-gray-50 border border-gray-200 rounded-2xl px-4 text-2xl text-center mb-4 focus:border-brand focus:ring-2 focus:ring-brand/20"
                    data-testid="emoji-picker-input"
                />
                {custom && (
                    <button
                        onClick={() => onPick(custom)}
                        className="w-full h-12 mb-4 rounded-full bg-brand text-white font-bold"
                        data-testid="emoji-picker-use-custom"
                    >
                        Usar "{custom}"
                    </button>
                )}

                <div className="text-[11px] uppercase tracking-wide font-bold text-gray-500 mb-2">Sugerencias</div>
                <div className="grid grid-cols-8 gap-2">
                    {EMOJI_SUGGESTIONS.map((e) => (
                        <button
                            key={e}
                            onClick={() => onPick(e)}
                            className={`aspect-square text-2xl rounded-xl hover:bg-gray-100 ${current === e ? 'bg-brand/10 ring-2 ring-brand' : ''}`}
                            data-testid={`emoji-suggestion-${e}`}
                        >
                            {e}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ComingSoonAttachments() {
    return (
        <div className="mt-5">
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-gray-800">Imágenes</label>
                <span className="text-xs text-gray-400">0/5 · Próximamente</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <button disabled className="h-12 rounded-2xl bg-gray-50 border border-dashed border-gray-300 text-gray-400 text-sm flex items-center justify-center gap-2 cursor-not-allowed" data-testid="new-list-upload-soon">
                    <Upload className="w-4 h-4" /> Subir
                </button>
                <button disabled className="h-12 rounded-2xl bg-gray-50 border border-dashed border-gray-300 text-gray-400 text-sm flex items-center justify-center gap-2 cursor-not-allowed" data-testid="new-list-photo-soon">
                    <Camera className="w-4 h-4" /> Foto
                </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Disponible en una próxima versión</p>
        </div>
    );
}

function ContactDraftSection({ contacts, setContacts }) {
    const [draft, setDraft] = useState({ name: '', role: '', phone: '', email: '' });
    const add = () => {
        if (!draft.name.trim()) return;
        setContacts([...contacts, { id: crypto.randomUUID(), ...draft }]);
        setDraft({ name: '', role: '', phone: '', email: '' });
    };
    return (
        <Field label="Contactos" rightLabel={`${contacts.length} contacto${contacts.length !== 1 ? 's' : ''}`}>
            <div className="space-y-2">
                {contacts.map((c, idx) => (
                    <div key={c.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl px-3 py-2" data-testid={`new-list-contact-${idx}`}>
                        <span className="w-9 h-9 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center">
                            {(c.name || '?').charAt(0).toUpperCase()}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-brand-navy truncate">{c.name}</div>
                            <div className="text-[11px] text-gray-500 truncate">{c.role || c.phone || c.email}</div>
                        </div>
                        <button onClick={() => setContacts(contacts.filter((_, i) => i !== idx))} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center">
                            <X className="w-4 h-4 text-gray-400" />
                        </button>
                    </div>
                ))}
                <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-3 space-y-2">
                    <TextInput value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Nombre" testId="new-list-contact-name" />
                    <TextInput value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="Rol o relación (ej. Plomero)" testId="new-list-contact-role" />
                    <TextInput value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="Teléfono" testId="new-list-contact-phone" />
                    <TextInput value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="Email" testId="new-list-contact-email" />
                    <button onClick={add} className="w-full h-11 rounded-full bg-brand/10 text-brand font-bold text-sm flex items-center justify-center gap-2" data-testid="new-list-contact-add">
                        <Plus className="w-4 h-4" /> Agregar contacto
                    </button>
                </div>
            </div>
        </Field>
    );
}
